import { InputWithLabel } from '@adotel/components'
import { useLazyQuery, useMutation } from '@apollo/client'
import {
  Button,
  CircularProgress,
  Divider,
  Typography
} from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'

import { addJob } from '../../cache/jobs'
import { EntitySelectionItemsBox } from '../../components/EntitySelection/EntitySelectionItemsBox'
import PageHeader from '../../components/PageHeader'
import SectionTitle from '../../components/SectionTitle'
import { ACCOMMODATIONS_QUERY } from '../../graphql/accommodation'
import { PROVIDERS_SUBMIT_LIST_QUERY } from '../../graphql/accommodationProviderSubmit'
import { BULKUPDATE_MUTATION } from '../../graphql/bulkUpdate'
import { CANCEL_POLICIES_QUERY } from '../../graphql/cancelPolicies'
import { MODIFY_POLICIES_QUERY } from '../../graphql/modifyPolicy'
import { RATEPLANS_QUERY } from '../../graphql/rateplan'
import { TENANTS_QUERY } from '../../graphql/tenant'
import { DateObject, getTodayDate } from '../../helpers/date'
import { convertPersianNumbersToEnglishNumbers } from '../../helpers/numbers'
import { filterAccommodationsByRatePlanQuery } from '../../helpers/ratePlanSearch'
import {
  getDefaultCurrency,
  getEntityCode,
  getEntityName,
  getRoleFromStorage
} from '../../helpers/userData'
import { getTranslate } from '../../localization'
import { JobsType } from '../../mockData/jobs'
import inventoryPagesRoleManagement from '../../staticRoleManagement/inventoryRoleManagement'
import {
  Accommodation,
  AccommodationDataWithRatePlans,
  RatePlanWithSelectoption
} from '../../types/accommodation'
import { ProviderSubmit } from '../../types/accommodationProviderSubmit'
import {
  AddOnForBulk,
  BulkUpdateData,
  BulkUpdateResponse,
  Multiplier,
  RateObject,
  TimePeriod
} from '../../types/bulkUpdate'
import { CancelPolicy, ModifyPolicy } from '../../types/cancelAndModifyPolicies'
import { Currency } from '../../types/currency'
import { AddOn, RatePlanBrief } from '../../types/rateplan'
import { Tenant } from '../../types/tenants'
import AddonsForBulkUpdate from './AddonsForBulkUpdate'
import InventoryBasicFilters from './InventoryBasicFilters'
import InventoryNonRestrictionItems from './InventoryNonRestrictionItems'
import InventoryRates from './InventoryRates'
import { Restrictions } from './InventoryRestrictionSelector'
import InventoryRestrictions from './InventoryRestrictions'
import useStyles from './styles/InventoryBulkUpdate.style'

function InventoryBulkUpdate() {
  const classes = useStyles()

  const hasAccessToChangeTenant = inventoryPagesRoleManagement
    .find((item) => item.route === '/inventory/:page')
    ?.sections?.SELECT_TENANT.rolesListWithWriteAccess?.includes(
      getRoleFromStorage()
    )

  const hasAccessToChangeAccommodation = inventoryPagesRoleManagement
    .find((item) => item.route === '/inventory/:page')
    ?.sections?.SELECT_ACCOMMODATION.rolesListWithWriteAccess?.includes(
      getRoleFromStorage()
    )

  const isRoleAccommodation = inventoryPagesRoleManagement
    .find((item) => item.route === '/inventory/:page')
    ?.sections?.IS_ACCOMMODATION.rolesListWithWriteAccess?.includes(
      getRoleFromStorage()
    )

  const isRoleProvider = inventoryPagesRoleManagement
    .find((item) => item.route === '/inventory/:page')
    ?.sections?.IS_PROVIDER.rolesListWithWriteAccess?.includes(
      getRoleFromStorage()
    )

  const [autoCompleteForTenant, setAutoCompleteForTenant] = useState<any>(null)

  const [autoCompleteForAccommodation, setAutoCompleteForAccommodation] =
    useState<AccommodationDataWithRatePlans[]>([])

  const [autoCompleteForProvider, setAutoCompleteForProvider] = useState<any>(
    isRoleProvider
      ? {
          label: getEntityName(),
          value: getEntityCode()
        }
      : null
  )

  const [filteredAccommodationsData, setFilteredAccommodationsData] = useState<
    AccommodationDataWithRatePlans[]
  >([])

  const [providerAddOns, setProviderAddOns] = useState<AddOn[]>([])
  const [selectedDates, setselectedDates] = useState<DateObject[]>([{}])
  const [isChannelSelected, setIsChannelSelected] = useState<string>('CRS')
  const [activeDatePickerIndex, setActiveDatePickerIndex] = useState(-1)
  const [cancelPolicyValue, setCancelPolicyValue] = useState<{
    label: string
    value: string
  } | null>(null)
  const [modifyPolicyValue, setModifyPolicyValue] = useState<{
    label: string
    value: string
  } | null>(null)
  const [rates, setRates] = useState<RateObject[]>([])
  // const [ratesChild, setRatesChild] = useState<RateObject[]>([])
  const [selectedChannel, setSelectedChannel] = useState<any[]>([])
  const [searchInputValue, setSearchInputValue] = useState<string>('')
  const [onDoneClickForCloseDatePicker, setOnDoneClickForCloseDatePicker] =
    useState<boolean>(true)
  const [timePickerSelected, setTimePickerSelected] = useState<boolean>(false)
  const [activeRestrictions, setActiveRestrictions] = useState<
    Record<Restrictions, { active: boolean; value?: boolean | string }>
  >({
    MasterStatus: { active: false },
    CTA: { active: false },
    CTD: { active: false },
    MinStay: { active: false },
    MaxStay: { active: false }
  })
  const [availability, setavailability] = useState('')
  const [rateMultipliers, setRateMultipliers] = useState<Multiplier[]>([
    {
      rateMultiplier: '',
      inputCurrency: getDefaultCurrency(),
      outputCurrency: getDefaultCurrency()
    }
  ])

  const [getTenants, { data: tenantsData, loading: loadingForTenantsList }] =
    useLazyQuery<{
      tenants: Tenant[]
    }>(TENANTS_QUERY)

  useEffect(() => {
    if (hasAccessToChangeTenant) {
      getTenants()
    }
  }, [])

  const [
    getCancelPolicyData,
    { data: cancelPolicyData, loading: loadingForCancelPolicy }
  ] = useLazyQuery<{
    cancelPolicies: CancelPolicy[]
  }>(CANCEL_POLICIES_QUERY, {
    notifyOnNetworkStatusChange: true
  })

  const [
    getModifyPolicyData,
    { data: modifyPolicyData, loading: loadingForModifyPolicy }
  ] = useLazyQuery<{
    modifyPolicies: ModifyPolicy[]
  }>(MODIFY_POLICIES_QUERY, {
    notifyOnNetworkStatusChange: true
  })

  const [getRatePlans, { loading: loadingForRatePlans }] = useLazyQuery<{
    ratePlans: RatePlanBrief[]
  }>(RATEPLANS_QUERY, {
    notifyOnNetworkStatusChange: true
  })
  const [accommodations, setaccommodations] = useState<Accommodation[]>([])

  const [getAccommodations, { loading: loadingForAccommodation }] =
    useLazyQuery<{
      accommodations: Accommodation[]
    }>(ACCOMMODATIONS_QUERY, {
      notifyOnNetworkStatusChange: true,
      onCompleted: (accommodationsData) => {
        setaccommodations(accommodationsData?.accommodations || [])
        if (isRoleAccommodation) {
          const newAccList: AccommodationDataWithRatePlans[] = [
            {
              label:
                accommodationsData?.accommodations[0].basicDetails.name || '',
              value:
                accommodationsData?.accommodations[0].basicDetails
                  .propertyCode || '',
              id:
                accommodationsData?.accommodations[0].basicDetails
                  .accommodationID || '',
              tenantCode:
                accommodationsData?.accommodations[0].basicDetails.tenantCode ||
                '',
              tenantID:
                accommodationsData?.accommodations[0].basicDetails.tenantId ||
                '',
              ratePlans: [],
              code: accommodationsData?.accommodations[0].basicDetails
                .propertyCode,
              propertyCode:
                accommodationsData?.accommodations[0].basicDetails.propertyCode
            }
          ]

          getRatePlans({
            variables: {
              propertyCode:
                accommodationsData?.accommodations[0].basicDetails.propertyCode,
              showDeletedRatePlans: false,
              showDeactivedRatePlans: false,
              providerCode:
                autoCompleteForProvider && autoCompleteForProvider.value
            }
          }).then(({ data }) => {
            newAccList.forEach((singleAcc) => {
              if (!singleAcc.ratePlans) {
                singleAcc.ratePlans = []
                setAutoCompleteForAccommodation([...newAccList])
              }
              singleAcc.ratePlans = data?.ratePlans
            })
            setAutoCompleteForAccommodation([...newAccList])
          })
        }
      }
    })

  const [
    getProviderConnectionList,
    { data: providerListData, loading: loadingForProviderSubmitList }
  ] = useLazyQuery<{
    providersSubmitList: ProviderSubmit[]
  }>(PROVIDERS_SUBMIT_LIST_QUERY, {
    notifyOnNetworkStatusChange: true,
    onError: (error) => {
      toast.error(error.message)
    }
  })

  useEffect(() => {
    if (isRoleAccommodation || hasAccessToChangeAccommodation) {
      getAccommodations()
    }
  }, [])

  useEffect(() => {
    if (autoCompleteForProvider && providerListData) {
      const filteredProvidersSubmitListAddOn =
        providerListData.providersSubmitList.filter(
          (provider) => provider.providerCode === autoCompleteForProvider.value
        )[0].addOns

      setProviderAddOns(filteredProvidersSubmitListAddOn)
    }
  }, [autoCompleteForProvider, providerListData])

  useEffect(() => {
    if (
      autoCompleteForAccommodation &&
      autoCompleteForAccommodation.length > 0
    ) {
      getProviderConnectionList({
        variables: {
          propertyCode: autoCompleteForAccommodation[0].value
        }
      })
    }
  }, [autoCompleteForAccommodation && autoCompleteForAccommodation.length])

  const [DoBulkUpdate, { loading: loadingForConfirm }] = useMutation<{
    bulkUpdate: BulkUpdateResponse
  }>(BULKUPDATE_MUTATION, {
    onCompleted: (response) => {
      if (response.bulkUpdate.trackingID) {
        const newJob: JobsType = {
          trackID: response.bulkUpdate.trackingID,
          creationDate: getTodayDate(),
          status: 'InProgress'
        }
        addJob(newJob)
        toast.info(getTranslate('Bulk update is in progress'))
      } else {
        toast.error(new Error(getTranslate('Server error, please try again')))
      }
    },
    onError: (err) => toast.error(err.message)
  })

  const accommodationsListForSelectedTenant: Accommodation[] = useMemo(
    () =>
      autoCompleteForTenant?.value && accommodations
        ? accommodations.filter(
            (singleAcc) =>
              singleAcc.basicDetails.displayTenantCode ===
              autoCompleteForTenant.code
          )
        : !hasAccessToChangeTenant && accommodations
        ? accommodations
        : [],
    [autoCompleteForTenant?.value, accommodations]
  )

  const handleChangeSelectedAccommodations = (
    newAccList: AccommodationDataWithRatePlans[]
  ) => {
    // The old query was written for the previous list and silently filters the
    // freshly loaded rate plans out — clear it when the selection changes.
    setSearchInputValue('')

    if (newAccList.length < autoCompleteForAccommodation.length) {
      setAutoCompleteForAccommodation((currentValue) =>
        currentValue.filter((item) =>
          newAccList.some((newItem) => newItem.value === item.value)
        )
      )

      return
    }

    const updatedAccList = newAccList.map((singleAcc) => {
      const index = autoCompleteForAccommodation.findIndex(
        (ele) => ele.id === singleAcc.id
      )

      if (!singleAcc.ratePlans && index === -1) {
        return {
          ...singleAcc,
          ratePlans: []
        }
      }
      return singleAcc
    })

    setAutoCompleteForAccommodation(updatedAccList)

    updatedAccList.forEach((singleAcc) => {
      if (!singleAcc.ratePlans) {
        getRatePlans({
          variables: {
            propertyCode: singleAcc.code,
            showDeletedRatePlans: false,
            showDeactivedRatePlans: false,
            providerCode:
              autoCompleteForProvider && autoCompleteForProvider.value
          }
        }).then(({ data }) => {
          const updatedRatePlans =
            data?.ratePlans && data?.ratePlans.length > 0 ? data.ratePlans : []

          setAutoCompleteForAccommodation((prevAccommodations) =>
            prevAccommodations.map((prevAcc) => {
              if (prevAcc.value === singleAcc.value) {
                return {
                  ...prevAcc,
                  ratePlans:
                    prevAcc.ratePlans && prevAcc.ratePlans.length > 0
                      ? prevAcc.ratePlans
                      : updatedRatePlans
                }
              }

              return prevAcc
            })
          )
        })
      }
    })
  }

  useEffect(() => {
    if (isChannelSelected === 'CRS') {
      setSelectedChannel([])
      setRateMultipliers([])
    } else {
      setRateMultipliers([
        {
          rateMultiplier: '',
          inputCurrency: getDefaultCurrency(),
          outputCurrency: getDefaultCurrency()
        }
      ])
    }
  }, [isChannelSelected])

  const handleConfirm = async () => {
    const existMultipliers = rateMultipliers.every((ele) => ele.rateMultiplier)

    const bulkUpdateData: BulkUpdateData = {
      availability:
        availability || availability === '0'
          ? parseInt(availability)
          : undefined,
      cancellationPolicySetID: cancelPolicyValue?.value,
      modifyPolicySetID: modifyPolicyValue?.value,
      closeToArrival: activeRestrictions.CTA.active
        ? (activeRestrictions.CTA.value as boolean)
        : undefined,
      closeToDeparture: activeRestrictions.CTD.active
        ? (activeRestrictions.CTD.value as boolean)
        : undefined,
      minStay: activeRestrictions.MinStay.active
        ? parseInt(activeRestrictions.MinStay.value as string)
        : undefined,
      maxStay: activeRestrictions.MaxStay.active
        ? parseInt(activeRestrictions.MaxStay.value as string)
        : undefined,
      status: activeRestrictions.MasterStatus.active
        ? (activeRestrictions.MasterStatus.value as boolean)
          ? 'Open'
          : 'Closed'
        : undefined,
      periods: selectedDates.map(
        (dateItem) =>
          ({
            from: dateItem.gregorianStartDate,
            to: dateItem.gregorianEndDate
          } as TimePeriod)
      ),
      ratePlans: autoCompleteForAccommodation
        .map((singleAcc) =>
          singleAcc
            .ratePlans!.filter((singleRatePlan) => singleRatePlan.selected)
            .map((singleRatePlan) => {
              const tempAddon: AddOnForBulk[] = []
              providerAddOns.forEach((ad) => {
                if (ad.amount) {
                  tempAddon.push({
                    amount: ad.amount,
                    code: ad.code,
                    currency: (ad.currency
                      ? ad.currency
                      : getDefaultCurrency()) as Currency,
                    type: ad.type
                  })
                }
              })

              return {
                propertyCode: singleAcc.value, //value is code here
                roomTypeCode: singleRatePlan.roomTypeCode,
                ratePlanCode: singleRatePlan.ratePlanCode || '',
                addOns: tempAddon
              }
            })
        )
        .flat(),
      rates: rates,
      tenantCode: autoCompleteForAccommodation[0].tenantCode
        ? autoCompleteForAccommodation[0].tenantCode.toString()
        : autoCompleteForTenant.code,
      tenantID: autoCompleteForAccommodation[0].tenantID
        ? autoCompleteForAccommodation[0].tenantID.toString()
        : autoCompleteForTenant.id,
      accommodationCode: isRoleAccommodation
        ? accommodations[0].basicDetails.propertyCode
        : undefined,
      channelCodes: selectedChannel.map((selectedChan) => selectedChan.code),
      multipliers: existMultipliers ? rateMultipliers : undefined,
      providerCode: autoCompleteForProvider && autoCompleteForProvider.value
    }

    DoBulkUpdate({
      variables: {
        input: bulkUpdateData
      }
    })
  }

  useEffect(() => {
    if (!loadingForProviderSubmitList) {
      if (autoCompleteForProvider) {
        getCancelPolicyData({
          variables: {
            providerCode: autoCompleteForProvider.value
          }
        })
      } else if (isRoleProvider) {
        getCancelPolicyData({
          variables: {
            providerCode: getEntityCode()
          }
        })
      }

      if (autoCompleteForProvider) {
        getModifyPolicyData({
          variables: {
            providerCode: autoCompleteForProvider.value
          }
        })
      } else if (isRoleProvider) {
        getModifyPolicyData({
          variables: {
            providerCode: getEntityCode()
          }
        })
      }
    }
  }, [autoCompleteForProvider, loadingForProviderSubmitList])

  const handleChangeAccommodationRatePlans = (
    callbackFunc: (
      currentData: RatePlanWithSelectoption[]
    ) => RatePlanWithSelectoption[],
    index: number
  ) => {
    setAutoCompleteForAccommodation((currentData) => {
      const newRatePlans = callbackFunc(currentData[index].ratePlans || [])
      return [
        ...currentData.slice(0, index),
        {
          ...currentData[index],
          ratePlans: newRatePlans
        },
        ...currentData.slice(index + 1)
      ]
    })
  }

  useEffect(() => {
    setFilteredAccommodationsData(
      filterAccommodationsByRatePlanQuery(
        autoCompleteForAccommodation,
        searchInputValue
      )
    )
  }, [searchInputValue, autoCompleteForAccommodation])

  const hasAccessToDoBulkUpdate = inventoryPagesRoleManagement
    .find((item) => item.route === '/inventory/:page')
    ?.tabs?.find((tabItem) => tabItem.params.page === 'bulk-update')
    ?.rolesListWithWriteAccess?.includes(getRoleFromStorage())

  return (
    <div className={classes.root}>
      <PageHeader title={getTranslate('Bulk Update')} />
      <InventoryBasicFilters
        {...{
          hasAccessToChangeTenant: !!hasAccessToChangeTenant,
          autoCompleteForTenant,
          setAutoCompleteForTenant,
          tenants: tenantsData?.tenants || [],
          loadingForTenantsList,
          setCancelPolicyValue,
          setModifyPolicyValue,
          hasAccessToChangeAccommodation: !!hasAccessToChangeAccommodation,
          loadingForAccommodation,
          autoCompleteForAccommodation,
          setAutoCompleteForAccommodation,
          handleChangeSelectedAccommodations,
          isChannelSelected,
          setIsChannelSelected,
          selectedChannel,
          setSelectedChannel,
          selectedDates,
          setselectedDates,
          activeDatePickerIndex,
          setActiveDatePickerIndex,
          timePickerSelected,
          setTimePickerSelected,
          onDoneClickForCloseDatePicker,
          setOnDoneClickForCloseDatePicker,
          loadingForProviderSubmitList,
          providerData: providerListData?.providersSubmitList,
          autoCompleteForProvider,
          setAutoCompleteForProvider,
          accommodationsListForSelectedTenant,
          getAccommodations,
          isRoleProvider
        }}
      />
      {autoCompleteForProvider &&
        autoCompleteForAccommodation &&
        autoCompleteForAccommodation.length > 0 && (
          <InputWithLabel
            placeholder={getTranslate(
              'Search in Room types, Rateplans or Rateplans Codes'
            )}
            autoComplete="off"
            noHelperText
            inputSize="small"
            startIcon={<SearchIcon />}
            value={searchInputValue}
            // While the rate plans for a freshly picked accommodation are
            // still loading, searching would filter an empty list and look
            // broken — hold the input until the data is there.
            disabled={loadingForRatePlans}
            onChange={(event) => {
              setSearchInputValue(
                convertPersianNumbersToEnglishNumbers(event.currentTarget.value)
              )
            }}
          />
        )}
      {((autoCompleteForAccommodation && autoCompleteForProvider) ||
        isRoleAccommodation) &&
        filteredAccommodationsData.map((item) => {
          // The filtered list can drop accommodations, so its indices no
          // longer line up with autoCompleteForAccommodation — resolve the
          // real index of this accommodation before mutating its rate plans.
          const sourceIndex = autoCompleteForAccommodation.findIndex(
            (source) => source.value === item.value
          )
          if (sourceIndex === -1) return null
          return (
            <div key={item.id + sourceIndex}>
              <EntitySelectionItemsBox
                accData={item}
                onChangeAccommodationRatePlans={(callbackFunc) =>
                  handleChangeAccommodationRatePlans(callbackFunc, sourceIndex)
                }
                providerCode={
                  autoCompleteForProvider ? autoCompleteForProvider.value : ''
                }
              />
            </div>
          )
        })}
      <Divider className={classes.DividerLine} />
      <SectionTitle title={getTranslate('Addons')} marginBottom />
      <div className={classes.restrictionOptionsContainer}>
        {autoCompleteForProvider &&
        providerAddOns &&
        providerAddOns.length > 0 ? (
          <AddonsForBulkUpdate
            providerAddOns={providerAddOns}
            setProviderAddOns={setProviderAddOns}
            providerName={
              autoCompleteForProvider && autoCompleteForProvider.label
            }
          />
        ) : (
          <Typography>
            {getTranslate('Please Select RatePlans To See The Addons')}
          </Typography>
        )}
      </div>
      <Divider className={classes.DividerLine} />
      <div className={classes.restrictionOptionsContainer}>
        <InventoryRestrictions
          {...{ activeRestrictions, setActiveRestrictions }}
        />
      </div>
      <Divider className={classes.DividerLine} />
      <div className={classes.nonRestrictionOptions}>
        <InventoryNonRestrictionItems
          {...{
            availability,
            setavailability,
            rateMultipliers,
            setRateMultipliers,
            isChannelSelected,
            setCancelPolicyValue,
            cancelPolicyValue,
            setModifyPolicyValue,
            modifyPolicyValue,
            cancelPolicyData,
            modifyPolicyData,
            loadingForCancelPolicy,
            loadingForModifyPolicy,
            isRoleAccommodation: !!isRoleAccommodation
          }}
        />
      </div>
      <Divider className={classes.DividerLine} />
      <div className={classes.restrictionOptionsContainer}>
        <InventoryRates
          {...{
            rates,
            setRates,
            title: getTranslate('Add Occupancy')
          }}
        />
      </div>
      <Divider className={classes.DividerLine} />
      {/* <div className={classes.restrictionOptionsContainer}>
        <InventoryRates
          {...{
            rates: ratesChild,
            setRates: setRatesChild,
            title: getTranslate('Add Child')
          }}
        />
      </div> */}
      {/* <Divider className={classes.DividerLine} /> */}
      <div className={classes.confirmButtonContainer}>
        <Button
          disabled={
            !hasAccessToDoBulkUpdate ||
            loadingForConfirm ||
            !!!selectedDates[0].gregorianStartDate ||
            !!!selectedDates[0].gregorianEndDate
          }
          variant="contained"
          color="primary"
          onClick={handleConfirm}>
          {getTranslate('Confirm')}
          {loadingForConfirm && (
            <CircularProgress
              className={classes.circularProgress}
              size={16}
              color="primary"
            />
          )}
        </Button>
      </div>
    </div>
  )
}

export default InventoryBulkUpdate
